import { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { IconSettings } from '@components/icons/icon-settings';
import { InputRadio } from '@components/input-radio';
import { ModalStickyHeader } from '@components/modals/modal-sticky-header';
import { ProtectedComponent } from '@components/protected-component';
import { ENABLED_SIDEBAR_MENU_USERS_MANAGEMENT_BUTTON } from '@constants/company-auth-permissions';
import { EVENT_LIST, USERS_ROUTE } from '@constants/routes';
import { CompanyClubContext } from '@contexts/company-club/company-club-context';
import { useGetUserCompanies } from '@hooks/user/user-get-companies';
import classNames from 'classnames';

import { Props } from './select-company.type';

export const SelectCompany = ({ user, handleClose, companySelected, clubState }: Props) => {
  const navigate = useNavigate();
  const { companyState } = useContext(CompanyClubContext);
  const { data: userCompanies, isPending } = useGetUserCompanies(clubState.id);
  const clubSlug = clubState.slug;

  const handleCompanyClick = (legacyId: string) => {
    navigate(EVENT_LIST.replace(':companyId', legacyId).replace(':clubSlug', clubSlug).replace(':view', 'upcoming'));
    navigate(0);
    handleClose();
  };

  const handleSettingsClick = (legacyId: string) => {
    // Another company needs a full reload: navigating to it alone leaves the app misbehaving.
    navigate(USERS_ROUTE.replace(':companyId', legacyId).replace(':clubSlug', clubSlug));
    if (companyState.legacyId !== legacyId) navigate(0);
  };

  const userProfile = user ? (
    <div className='flex flex-row items-center gap-3'>
      <img className='rounded-full w-16 h-16' src={user.profileImageUrl} alt='profile' />
      <p className='text-left text-primary text-headline-l'>{user.firstName}</p>
    </div>
  ) : (
    ''
  );

  return (
    <>
      <ModalStickyHeader title={userProfile} handleClose={handleClose} bordered={true} />
      <div className='flex flex-col p-4 sm:px-10 sm:py-10 overflow-y-auto'>
        <div className='space-y-3 w-full'>
          {isPending &&
            Array.from({ length: 3 }, (_, index) => (
              <div
                key={`skeleton-${index}`}
                className='flex items-center w-full shadow-md rounded-md py-3 px-4 dark:shadow-black-1000/30 animate-pulse'>
                <div className='w-5 h-5 rounded-full bg-tertiary shrink-0 mr-3' />
                <div className='flex flex-col gap-1.5'>
                  <div className='h-4 w-40 rounded-sm bg-tertiary' />
                  <div className='h-3.5 w-28 rounded-sm bg-tertiary' />
                  <div className='h-3 w-16 rounded-sm bg-tertiary' />
                </div>
              </div>
            ))}
          {!isPending &&
            userCompanies?.map((company) => {
              const isSelected = companySelected.id === company.id;
              return (
                <button
                  key={company.id}
                  className='w-full'
                  type='button'
                  onClick={() => !isSelected && handleCompanyClick(company?.legacyId)}>
                  <div
                    className={classNames(
                      'flex item-center w-full shadow-md rounded-md py-3 px-4 dark:shadow-black-1000/30',
                      {
                        'cursor-default': isSelected,
                        'cursor-pointer': !isSelected,
                      }
                    )}>
                    <InputRadio defaultChecked={isSelected} />
                    <div>
                      <p className='text-subtitle-m text-primary text-left'>{company.name}</p>
                      <p className='text-body-s text-left text-secondary'>{company.commercialName}</p>
                      <p className='text-body-xs text-tertiary text-left'>ID {company.legacyId}</p>
                    </div>
                    <ProtectedComponent permissions={ENABLED_SIDEBAR_MENU_USERS_MANAGEMENT_BUTTON}>
                      <IconSettings
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSettingsClick(company?.legacyId);
                        }}
                        className='ml-auto self-center w-8 h-8 text-tertiary hover:text-primary cursor-pointer rounded-full p-1 hover:bg-tertiary transition-colors'
                      />
                    </ProtectedComponent>
                  </div>
                </button>
              );
            })}
        </div>
      </div>
    </>
  );
};
